import { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { Link as RouterLink } from "react-router-dom";

import { Box, Typography, IconButton, useTheme, Link, Button } from "@mui/material";

import ArrowForward from '@mui/icons-material/ArrowForward';
import Close from "@mui/icons-material/Close";

export const Guide = ({ topic, onClose }) => {
    const [ sections, setSections ] = useState([]);
    const [ expandedSection, setExpandedSection ] = useState(null);
    const theme = useTheme();

    useEffect(() => {
        fetch(`/help/${ topic }.md`)
            .then(res => res.text())
            .then(content => {
                // Parse sections from content
                const lines = content.split('\n');
                const sections = [];
                let currentSection = null;
                let currentContent = [];
                
                for (const line of lines) {
                    if (line.startsWith('## ')) {
                        // Save previous section
                        if (currentSection) {
                            sections.push({
                                title: currentSection,
                                content: currentContent.join('\n')
                            });
                        }
                        // Start new section
                        currentSection = line.substring(3).trim();
                        currentContent = [line]; // Include the header
                    } else if (currentSection) {
                        currentContent.push(line);
                    }
                }
                
                // Save last section
                if (currentSection) {
                    sections.push({
                        title: currentSection,
                        content: currentContent.join('\n')
                    });
                }
                
                setSections(sections);
            });
    }, [ topic ]);

    return (
        <Box 
            data-guide-container
            sx={ { 
                p: { xs: 2, sm: 3 }, 
                height: { xs: '100vh', sm: '100%' }, 
                width: { xs: '100vw', sm: 'auto' },
                overflow: 'auto',
                position: { xs: 'fixed', sm: 'static' },
                top: { xs: 0, sm: 'auto' },
                left: { xs: 0, sm: 'auto' },
                zIndex: { xs: 1300, sm: 'auto' },
                bgcolor: { xs: 'background.paper', sm: 'transparent' },
                boxSizing: 'border-box'
            } }
        >
            <Box sx={ { display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 } }>
                <Typography variant="h5" component="h1">
                    Patrol360 Guide
                </Typography>
                
                <IconButton onClick={ onClose } size="small">
                    <Close />
                </IconButton>
            </Box>

            {sections.map((section, index) => {
                const sectionId = section.title.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
                const isExpanded = expandedSection === sectionId;

                return (
                    <Box key={index} sx={{ mb: 1 }}>
                        <Box
                            onClick={() => {
                                const newExpandedSection = isExpanded ? null : sectionId;
                                setExpandedSection(newExpandedSection);
                                // Scroll to the section header after state update
                                setTimeout(() => {
                                    const sectionElement = document.getElementById(sectionId);
                                    if (sectionElement) {
                                        sectionElement.scrollIntoView({ 
                                            behavior: 'smooth',
                                            block: 'start'
                                        });
                                    }
                                }, 0);
                            }}
                            sx={{
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 2,
                                borderRadius: 1,
                                border: `1px solid ${theme.palette.divider}`,
                                bgcolor: isExpanded ? theme.palette.action.selected : 'transparent',
                                '&:hover': {
                                    bgcolor: theme.palette.action.hover
                                }
                            }}
                        >
                            <Typography 
                                id={sectionId}
                                variant="h5" 
                                component="h2" 
                                sx={{ 
                                    wordWrap: 'break-word', 
                                    overflowWrap: 'break-word',
                                    wordBreak: 'break-word',
                                    whiteSpace: 'normal'
                                }}
                            >
                                {section.title}
                            </Typography>
                            <Typography variant="h6" sx={{ color: 'text.secondary', ml: 1 }}>
                                {isExpanded ? '−' : '+'}
                            </Typography>
                        </Box>
                        
                        {isExpanded && (
                            <Box sx={{ mt: 1, p: 2, border: `1px solid ${theme.palette.divider}`, borderTop: 'none', borderRadius: '0 0 4px 4px' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                                    <Link
                                        component="button"
                                        onClick={() => {
                                            const guideElement = document.querySelector('[data-guide-container]');
                                            if (guideElement) {
                                                guideElement.scrollTo({ top: 0, behavior: 'smooth' });
                                            } else {
                                                window.scrollTo({ top: 0, behavior: 'smooth' });
                                            }
                                        }}
                                        sx={{ 
                                            fontSize: '0.75rem',
                                            color: theme.palette.text.secondary,
                                            textDecoration: 'none',
                                            background: 'none',
                                            border: 'none',
                                            padding: 0,
                                            cursor: 'pointer',
                                            '&:hover': {
                                                color: theme.palette.primary.main,
                                                textDecoration: 'underline'
                                            }
                                        }}
                                    >
                                        ↑ Back to Top
                                    </Link>
                                </Box>
                                
                                <ReactMarkdown
                                    components={{
                                        h1: ({ children }) => {
                                            const id = typeof children === 'string' ? children.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '') : '';
                                            return (
                                                <Typography 
                                                    id={id}
                                                    variant="h4" 
                                                    component="h1" 
                                                    gutterBottom 
                                                    sx={{ 
                                                        wordWrap: 'break-word', 
                                                        overflowWrap: 'break-word',
                                                        wordBreak: 'break-word',
                                                        whiteSpace: 'normal'
                                                    }}
                                                >
                                                    { children }
                                                </Typography>
                                            );
                                        },
                                        h2: ({ children }) => {
                                            const id = typeof children === 'string' ? children.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '') : '';
                                            return (
                                                <Typography 
                                                    id={id}
                                                    variant="h5" 
                                                    component="h2" 
                                                    gutterBottom 
                                                    sx={{ 
                                                        wordWrap: 'break-word', 
                                                        overflowWrap: 'break-word',
                                                        wordBreak: 'break-word',
                                                        whiteSpace: 'normal',
                                                        mt: 3,
                                                        mb: 1
                                                    }}
                                                >
                                                    { children }
                                                </Typography>
                                            );
                                        },
                                        h3: ({ children }) => {
                                            const id = typeof children === 'string' ? children.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '') : '';
                                            return (
                                                <Typography 
                                                    id={id}
                                                    variant="h6" 
                                                    component="h3" 
                                                    gutterBottom 
                                                    sx={{ 
                                                        wordWrap: 'break-word', 
                                                        overflowWrap: 'break-word',
                                                        wordBreak: 'break-word',
                                                        whiteSpace: 'normal',
                                                        mt: 2
                                                    }}
                                                >
                                                    { children }
                                                </Typography>
                                            );
                                        },
                                        p: ({ children }) => (
                                            <Typography 
                                                variant="body1" 
                                                paragraph 
                                                sx={{ 
                                                    wordWrap: 'break-word', 
                                                    overflowWrap: 'break-word',
                                                    wordBreak: 'break-word',
                                                    whiteSpace: 'normal',
                                                    hyphens: 'auto'
                                                }}
                                            >
                                                { children }
                                            </Typography>
                                        ),
                                        ul: ({ children }) => <Box component="ul" sx={{ pl: 4, mb: 2 }}>{ children }</Box>,
                                        ol: ({ children }) => <Box component="ol" sx={{ pl: 4, mb: 2 }}>{ children }</Box>,
                                        li: ({ children }) => (
                                            <Typography 
                                                component="li" 
                                                variant="body1" 
                                                sx={{ 
                                                    mb: 0.5, 
                                                    wordWrap: 'break-word', 
                                                    overflowWrap: 'break-word',
                                                    wordBreak: 'break-word',
                                                    whiteSpace: 'normal'
                                                }}
                                            >
                                                { children }
                                            </Typography>
                                        ),
                                        strong: ({ children }) => <Box component="strong" sx={{ fontWeight: 'bold' }}>{ children }</Box>,
                                        code: ({ children }) => (
                                            <Box
                                                component="code"
                                                sx={{
                                                    bgcolor: theme.palette.grey[100],
                                                    px: 1,
                                                    py: 0.5,
                                                    borderRadius: 1,
                                                    fontFamily: 'monospace'
                                                }}
                                            >
                                                { children }
                                            </Box>
                                        ),
                                        blockquote: ({ children }) => (
                                            <Box
                                                sx={{
                                                    borderLeft: `4px solid ${theme.palette.primary.main}`,
                                                    pl: 3,
                                                    py: 1,
                                                    my: 2,
                                                    bgcolor: theme.palette.grey[50],
                                                    wordWrap: 'break-word',
                                                    overflowWrap: 'break-word',
                                                    wordBreak: 'break-word',
                                                    whiteSpace: 'normal'
                                                }}
                                            >
                                                { children }
                                            </Box>
                                        ),
                                        a: ({ children, href }) => {
                                            const handleClick = (e) => {
                                                if (href && href.startsWith('#')) {
                                                    e.preventDefault();
                                                    const targetId = href.substring(1);
                                                    const targetElement = document.getElementById(targetId);
                                                    if (targetElement) {
                                                        targetElement.scrollIntoView({ 
                                                            behavior: 'smooth',
                                                            block: 'start'
                                                        });
                                                    }
                                                }
                                            };

                                            return (
                                                <Link
                                                    href={href}
                                                    onClick={handleClick}
                                                    target={href && (href.startsWith('http') || href.startsWith('//')) ? '_blank' : undefined}
                                                    rel={href && (href.startsWith('http') || href.startsWith('//')) ? 'noopener noreferrer' : undefined}
                                                    sx={{ 
                                                        color: theme.palette.primary.main,
                                                        textDecoration: 'none',
                                                        '&:hover': {
                                                            textDecoration: 'underline'
                                                        }
                                                    }}
                                                >
                                                    { children }
                                                </Link>
                                            );
                                        }
                                    }}
                                >
                                    {section.content}
                                </ReactMarkdown>
                            </Box>
                        )}
                    </Box>
                );
            })}
            
            <Box sx={{ mt: 4, pt: 2, borderTop: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                    Found an issue with this guide?
                </Typography>

                <RouterLink 
                    to="/issue" 
                    style={{ textDecoration: 'none' }}
                    onClick={onClose}
                >
                    <Button variant="outlined" endIcon={ <ArrowForward /> }>
                        Let us know!
                    </Button>
                </RouterLink>
            </Box>
        </Box>
    );
}